/**
 * iter-309: Vendor Submissions page.
 *
 * Vendors submit Quote / Estimate / Running Bill with multiple line items.
 * Owner/Admin can Approve/Reject; Staff + Vendor (own) can View.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Plus, FileText, Receipt, Quote as QuoteIcon, Check, X, Eye, Trash2, ArrowLeft,
  ChevronDown, ChevronUp, Mail, Link as LinkIcon, Copy, Share2, Send, Folder,
  LayoutDashboard, GripVertical, Wallet,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNITS = ['Nos', 'Sqft', 'Sqm', 'Rmt', 'Ltr', 'Kg', 'Lot', 'Day', 'Hour', 'Set', 'Lump'];
const GST_OPTIONS = [0, 5, 12, 18, 28];

const TYPES = [
  { id: 'quote', label: 'Quote', icon: QuoteIcon },
  { id: 'estimate', label: 'Estimate', icon: FileText },
  { id: 'bill', label: 'Running Bill', icon: Receipt },
];

const STATUS_COLORS = {
  draft: 'bg-stone-200 text-stone-700',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

function formatINR(n) {
  const v = Math.round(+n || 0);
  return `Rs ${v.toLocaleString('en-IN')}`;
}

// iter-386u-k — Vendor-driven Ad-hoc Submission Link (relocated from
// VendorDashboard). Lets the assigned vendor spin up a one-off public
// /p/vendor-submit/{token} URL for one of their own projects. Backend
// reuses an active link if one already exists.
function VendorAdhocLinkCard({ projects }) {
  const [projectId, setProjectId] = useState('');
  const [submissionType, setSubmissionType] = useState('quote');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    if (!projectId) return;
    setCreating(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/vendor/adhoc-submission-link`, {
        project_id: projectId,
        submission_type: submissionType,
        base_url: typeof window !== 'undefined' ? window.location.origin : null,
      });
      setResult(r.data);
      toast.success(r.data.reused ? 'Reused existing active link' : 'Ad-hoc submission link ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    if (!result?.public_url) return;
    try {
      await navigator.clipboard.writeText(result.public_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success('Copied to clipboard');
    } catch { toast.error('Could not copy'); }
  };

  return (
    <Card className="mb-4 border-purple-200" data-testid="vendor-adhoc-link-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 rounded-lg bg-purple-100">
            <Wallet size={18} className="text-purple-700" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900">Ad-hoc Submission Link</h3>
            <p className="text-[11px] text-stone-500">Generate a quick public link to submit a Quote / Estimate / Running Bill for one of your projects.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_100px] gap-2">
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="h-9 border border-stone-300 rounded-md px-2 text-sm"
            data-testid="vendor-adhoc-project"
          >
            <option value="">Select project…</option>
            {(projects || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select
            value={submissionType}
            onChange={(e) => setSubmissionType(e.target.value)}
            className="h-9 border border-stone-300 rounded-md px-2 text-sm"
            data-testid="vendor-adhoc-type"
          >
            <option value="quote">Quote</option>
            <option value="estimate">Estimate</option>
            <option value="bill">Running Bill</option>
          </select>
          <button
            type="button"
            onClick={create}
            disabled={!projectId || creating}
            className="h-9 rounded-md text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40"
            data-testid="vendor-adhoc-generate-btn"
          >
            {creating ? '…' : 'Generate'}
          </button>
        </div>
        {result?.public_url && (
          <div className="mt-3 p-2 bg-stone-50 border border-stone-200 rounded flex items-center gap-2">
            <input
              readOnly
              value={result.public_url}
              className="flex-1 bg-transparent text-xs font-mono text-stone-700 outline-none"
              data-testid="vendor-adhoc-link-url"
            />
            <button
              type="button"
              onClick={copyLink}
              className="text-xs px-2 py-1 rounded font-semibold bg-stone-900 text-white hover:bg-stone-800"
              data-testid="vendor-adhoc-copy-btn"
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
            {/* iter-386u-q — Open the generated link in a new tab. */}
            <button
              type="button"
              onClick={() => window.open(result.public_url, '_blank', 'noopener,noreferrer')}
              className="w-8 h-7 inline-flex items-center justify-center rounded border border-stone-300 text-stone-700 hover:bg-stone-100"
              title="Open link in new tab"
              data-testid="vendor-adhoc-open-btn"
            >
              <LinkIcon size={13} />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function VendorSubmissionsContent({ embedded = false }) {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const role = user?.role;
  const isAdmin = role === 'owner' || role === 'admin';
  // eslint-disable-next-line no-unused-vars
  const isStaffOrAdmin = ['owner', 'admin', 'staff'].includes(role);
  const isVendor = role === 'vendor';

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', submission_type: '' });

  const [categories, setCategories] = useState([]);  // main categories
  const [subByMain, setSubByMain] = useState({});    // {main_id: [subcategories]}
  const [projects, setProjects] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  // iter-311: Add to BOQ state
  const [addBOQOpen, setAddBOQOpen] = useState(false);
  const [addBOQTarget, setAddBOQTarget] = useState(null);
  const [addBOQMode, setAddBOQMode] = useState('new');
  const [addBOQName, setAddBOQName] = useState('');
  const [addBOQWorksheetId, setAddBOQWorksheetId] = useState('');
  const [projectWorksheets, setProjectWorksheets] = useState([]);
  const [addBOQSaving, setAddBOQSaving] = useState(false);
  // iter-312q: "Attach to project" — for standalone (project_id=null) submissions.
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const [attachProjectId, setAttachProjectId] = useState('');
  const [attachSaving, setAttachSaving] = useState(false);
  // iter-312t: "Apply to more projects" — multi-link a submission to extra projects.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkSelected, setLinkSelected] = useState([]); // additional project_ids to link
  const [linkSaving, setLinkSaving] = useState(false);
  // Expand state for description/remarks cells (BOQ-style)
  const [expandedDescs, setExpandedDescs] = useState(new Set());
  const [expandedRemarks, setExpandedRemarks] = useState(new Set());
  const toggleSet = (setter) => (key) => setter(prev => {
    const s = new Set(prev);
    if (s.has(key)) s.delete(key); else s.add(key);
    return s;
  });
  const toggleDesc = toggleSet(setExpandedDescs);
  const toggleRemarks = toggleSet(setExpandedRemarks);

  // iter-312b: Convert ad-hoc submission to registered vendor
  const [convertSaving, setConvertSaving] = useState(false);

  const doConvertAdhoc = async (sub) => {
    if (!sub?.id) return;
    if (!sub.vendor_email) {
      toast.error('No email on file for this ad-hoc vendor — cannot convert.');
      return;
    }
    const ok = window.confirm(
      `Convert "${sub.vendor_name}" (${sub.vendor_email}) into a registered Vendor user?\n\n• A vendor account will be created.\n• They'll receive a password-setup email.\n• This + any other ad-hoc submissions from the same link / email will be linked to the new vendor.`
    );
    if (!ok) return;
    setConvertSaving(true);
    try {
      const r = await axios.post(`${API}/vendor-submissions/${sub.id}/convert-to-vendor`, { send_password_set_email: true });
      const msg = r.data.already_existed
        ? `Linked ${r.data.submissions_linked} submission(s) to existing vendor (${r.data.email}).`
        : `Vendor account created. ${r.data.password_email_sent ? 'Password-setup email sent.' : 'Account created (email send failed — share the password reset link manually).'} Linked ${r.data.submissions_linked} submission(s).`;
      toast.success(msg);
      fetchAll();
      if (viewing?.id === sub.id) setViewOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertSaving(false);
    }
  };

  // iter-312: Email Preview state (used from both Edit form and View dialog)
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({ to_email: '', cc_email: '', subject: '', message: '' });
  const [emailSending, setEmailSending] = useState(false);

  const openEmailPreview = (sub) => {
    if (!sub?.id) {
      toast.error('Save the submission first (use Save as Draft) before emailing a preview.');
      return;
    }
    setEmailTarget(sub);
    const typeLbl = sub.submission_type === 'bill' ? 'Running Bill' : (sub.submission_type[0].toUpperCase() + sub.submission_type.slice(1));
    setEmailForm({
      to_email: '',
      cc_email: '',
      subject: `${typeLbl} preview: ${sub.title}`,
      message: '',
    });
    setEmailOpen(true);
  };

  const sendEmailPreview = async () => {
    if (!emailTarget) return;
    if (!emailForm.to_email.trim()) { toast.error('Recipient email is required'); return; }
    setEmailSending(true);
    try {
      await axios.post(`${API}/vendor-submissions/${emailTarget.id}/email-preview`, emailForm);
      toast.success(`Preview emailed to ${emailForm.to_email}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  // iter-312: Ad-hoc Vendor Share Links (admin-only)
  const [linksOpen, setLinksOpen] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ submission_type: 'quote', project_id: '', expiry_days: 14, note: '' });
  const [genSaving, setGenSaving] = useState(false);
  const [linkEmailOpen, setLinkEmailOpen] = useState(false);
  const [linkEmailTarget, setLinkEmailTarget] = useState(null);
  const [linkEmailForm, setLinkEmailForm] = useState({ to_email: '', recipient_name: '', message: '' });
  const [linkEmailSending, setLinkEmailSending] = useState(false);

  const fetchShareLinks = async () => {
    if (!isAdmin) return;
    setLinksLoading(true);
    try {
      const r = await axios.get(`${API}/vendor-share-links`);
      setShareLinks(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load links');
    } finally {
      setLinksLoading(false);
    }
  };

  const createShareLink = async () => {
    setGenSaving(true);
    try {
      const r = await axios.post(`${API}/vendor-share-links`, { ...genForm, base_url: window.location.origin });
      toast.success('Ad-hoc link generated');
      setGenOpen(false);
      setGenForm({ submission_type: 'quote', project_id: '', expiry_days: 14, note: '' });
      setShareLinks(prev => [r.data, ...prev]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create link');
    } finally {
      setGenSaving(false);
    }
  };

  const deleteShareLink = async (id) => {
    if (!window.confirm('Delete this link permanently? Vendors with this URL will no longer be able to submit. (Already-submitted entries remain intact.)')) return;
    try {
      await axios.delete(`${API}/vendor-share-links/${id}`);
      setShareLinks(prev => prev.filter(l => l.id !== id));
      toast.success('Link deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // iter-312f: One-click resend of an already-emailed link to the original recipient
  const [resendingId, setResendingId] = useState(null);
  const resendShareLink = async (link) => {
    if (!link?.last_emailed_to) {
      toast.error('No previous recipient on this link — use the Email action first.');
      return;
    }
    if (!window.confirm(`Resend this link to ${link.last_emailed_to}?\n\nThey'll receive a friendly reminder email with the same URL.`)) return;
    setResendingId(link.id);
    try {
      const r = await axios.post(`${API}/vendor-share-links/${link.id}/resend`, { base_url: window.location.origin });
      toast.success(`Reminder sent to ${r.data.to}`);
      // Bump email_count locally for instant feedback
      setShareLinks(prev => prev.map(l => l.id === link.id ? { ...l, email_count: (l.email_count || 0) + 1, last_emailed_at: new Date().toISOString() } : l));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend');
    } finally {
      setResendingId(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed — select the URL manually');
    }
  };

  const openLinkEmail = (link) => {
    setLinkEmailTarget(link);
    const typeLbl = link.submission_type === 'bill' ? 'Running Bill' : (link.submission_type[0].toUpperCase() + link.submission_type.slice(1));
    setLinkEmailForm({
      to_email: '',
      recipient_name: '',
      message: `Hi,\n\nPlease use the link below to submit your ${typeLbl} for ${link.project_name || 'our review'}.\n\nThanks.`,
    });
    setLinkEmailOpen(true);
  };

  const sendLinkEmail = async () => {
    if (!linkEmailTarget) return;
    if (!linkEmailForm.to_email.trim()) { toast.error('Recipient email is required'); return; }
    setLinkEmailSending(true);
    try {
      await axios.post(`${API}/vendor-share-links/${linkEmailTarget.id}/email`, { ...linkEmailForm, base_url: window.location.origin });
      toast.success(`Link emailed to ${linkEmailForm.to_email}`);
      setLinkEmailOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setLinkEmailSending(false);
    }
  };

  const blankItem = () => ({
    main_category_id: '', main_category_name: '',
    sub_category_id: '', sub_category_name: '',
    task_title: '', description: '',
    quantity: 1, unit: 'Nos', rate: 0,
    // iter-311: Est vs Actual tracking
    actual_quantity: null, actual_rate: null,
    gst_percentage: 0, discount_percentage: 0,
    notes: '',
    item_type: 'item', is_header: false,
  });
  // iter-315c: ad-hoc header row in Edit Vendor Submission modal
  const blankHeader = () => ({
    main_category_id: '', main_category_name: '',
    sub_category_id: '', sub_category_name: '',
    task_title: '', description: '',
    quantity: 0, unit: '', rate: 0,
    actual_quantity: null, actual_rate: null,
    gst_percentage: 0, discount_percentage: 0,
    notes: '',
    item_type: 'header', is_header: true,
  });

  const [form, setForm] = useState({
    submission_type: 'quote',
    project_id: '',
    title: '',
    notes: '',
    items: [blankItem()],
  });

  useEffect(() => {
    fetchAll();
    fetchCategories();
    fetchProjects();
  }, []);  // eslint-disable-line

  // iter-386u-aa — deep-link focus support. When another page navigates here
  // with ?focus=<sub_id>, scroll the row into view and pop the detail drawer
  // so the vendor lands directly on the submission that was clicked.
  const location = useLocation();
  useEffect(() => {
    if (!submissions?.length) return;
    const params = new URLSearchParams(location.search);
    const focusId = params.get('focus');
    if (!focusId) return;
    const target = submissions.find(s => s.id === focusId);
    if (!target) return;
    setViewing(target);
    setViewOpen(true);
    // Give the drawer time to mount then scroll the row into view
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="sub-row-${focusId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }, [submissions, location.search]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vendor-submissions`);
      setSubmissions(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const r = await axios.get(`${API}/boq-categories/main`);
      setCategories(r.data || []);
    } catch {/* silent */}
  };

  const fetchSubs = async (mainId) => {
    if (!mainId || subByMain[mainId]) return;
    try {
      const r = await axios.get(`${API}/boq-categories/sub`, { params: { main_category_id: mainId } });
      setSubByMain(prev => ({ ...prev, [mainId]: r.data || [] }));
    } catch {/* silent */}
  };

  const fetchProjects = async () => {
    try {
      const r = await axios.get(`${API}/projects`);
      setProjects(r.data || []);
    } catch {/* silent */}
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({
      submission_type: 'quote',
      project_id: '',
      title: '',
      notes: '',
      items: [blankItem()],
    });
    setFormOpen(true);
  };

  const openEdit = async (s) => {
    setEditingId(s.id);
    setForm({
      submission_type: s.submission_type,
      project_id: s.project_id || '',
      title: s.title,
      notes: s.notes || '',
      items: (s.items || []).map(it => ({ ...blankItem(), ...it })),
    });
    // Preload subcategories for any main categories already used.
    const mainIds = [...new Set((s.items || []).map(it => it.main_category_id).filter(Boolean))];
    await Promise.all(mainIds.map(fetchSubs));
    setFormOpen(true);
  };

  const updateItem = (idx, field, value) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => {
        if (i !== idx) return it;
        const nx = { ...it, [field]: value };
        if (field === 'main_category_id') {
          const cat = categories.find(c => c.id === value);
          nx.main_category_name = cat?.name || '';
          nx.sub_category_id = '';
          nx.sub_category_name = '';
          fetchSubs(value);
        }
        if (field === 'sub_category_id') {
          const subs = subByMain[nx.main_category_id] || [];
          const sub = subs.find(s => s.id === value);
          nx.sub_category_name = sub?.name || '';
        }
        return nx;
      }),
    }));
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, blankItem()] }));
  const addHeader = () => setForm(f => ({ ...f, items: [...f.items, blankHeader()] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // iter-315c: drag-reorder for headers + items in Edit Vendor Submission modal
  const [dragRowIdx, setDragRowIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const handleDragStart = (idx) => (e) => {
    setDragRowIdx(idx);
    try { e.dataTransfer.effectAllowed = 'move'; } catch {}
  };
  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragRowIdx === null || dragRowIdx === idx) return;
    setDragOverIdx(idx);
    try { e.dataTransfer.dropEffect = 'move'; } catch {}
  };
  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragRowIdx === null || dragRowIdx === idx) {
      setDragRowIdx(null); setDragOverIdx(null); return;
    }
    setForm(f => {
      const arr = [...f.items];
      const [moved] = arr.splice(dragRowIdx, 1);
      arr.splice(idx, 0, moved);
      return { ...f, items: arr };
    });
    setDragRowIdx(null); setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDragRowIdx(null); setDragOverIdx(null); };

  const totals = useMemo(() => {
    let subtotal = 0, gst = 0, disc = 0;
    let aSub = 0, aGst = 0, aDisc = 0;
    for (const it of form.items) {
      // iter-315c: section-header rows skipped from totals
      if (it.item_type === 'header' || it.is_header) continue;
      const q = +it.quantity || 0;
      const r = +it.rate || 0;
      const basic = q * r;
      const d = basic * (+it.discount_percentage || 0) / 100;
      const g = (basic - d) * (+it.gst_percentage || 0) / 100;
      subtotal += basic; disc += d; gst += g;
      // iter-311 actuals (fallback to estimated when blank)
      const aq = it.actual_quantity != null ? +it.actual_quantity : q;
      const ar = it.actual_rate != null ? +it.actual_rate : r;
      const aBasic = aq * ar;
      const aD = aBasic * (+it.discount_percentage || 0) / 100;
      const aG = (aBasic - aD) * (+it.gst_percentage || 0) / 100;
      aSub += aBasic; aDisc += aD; aGst += aG;
    }
    const est = subtotal - disc + gst;
    const act = aSub - aDisc + aGst;
    return { subtotal, disc, gst, total: est, aSub, aDisc, aGst, actTotal: act, variance: act - est };
  }, [form.items]);

  const handleSubmit = async (e, status = 'submitted') => {
    e?.preventDefault?.();
    // Validation
    if (!form.title.trim()) { toast.error('Enter a title'); return; }

    setSaving(true);
    try {
      // iter-315c: header rows allowed without category — only real items need it
      const isHeader = (it) => it.item_type === 'header' || it.is_header;
      let itemsToSave = form.items;
      if (status === 'draft') {
        itemsToSave = form.items.filter(it => isHeader(it)
          ? (it.task_title || '').trim()
          : ((it.task_title || '').trim() && it.main_category_id));
        const realCount = itemsToSave.filter(it => !isHeader(it)).length;
        if (realCount === 0) { toast.error('Add at least one line item (with Title + Category) before saving a draft'); return; }
      } else {
        const bad = form.items.findIndex(it => !isHeader(it) && (!it.task_title?.trim() || !it.main_category_id));
        if (bad >= 0) { toast.error(`Row ${bad + 1}: select Main Category and add Task title`); return; }
        const realCount = form.items.filter(it => !isHeader(it)).length;
        if (realCount === 0) { toast.error('Add at least one real line item — headers alone are not enough'); return; }
      }
      const payload = { ...form, items: itemsToSave, status };
      if (editingId) await axios.put(`${API}/vendor-submissions/${editingId}`, payload);
      else await axios.post(`${API}/vendor-submissions`, payload);
      toast.success(editingId ? 'Submission updated' : `Submission ${status === 'draft' ? 'saved as draft' : 'submitted'}`);
      setFormOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const doApprove = async (s) => {
    const note = window.prompt(`Approve "${s.title}"?\nOptional review note:`, '');
    if (note === null) return;
    try {
      await axios.post(`${API}/vendor-submissions/${s.id}/approve`, null, { params: note ? { review_notes: note } : {} });
      toast.success('Approved');
      fetchAll();
      if (viewing?.id === s.id) setViewOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };

  const doReject = async (s) => {
    const note = window.prompt(`Reject "${s.title}"?\nOptional reason:`, '');
    if (note === null) return;
    try {
      await axios.post(`${API}/vendor-submissions/${s.id}/reject`, null, { params: note ? { review_notes: note } : {} });
      toast.success('Rejected');
      fetchAll();
      if (viewing?.id === s.id) setViewOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    }
  };

  const doDelete = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/vendor-submissions/${s.id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // iter-315d: Convert the current submission to a PDF (download) — uses the
  // existing /preview-pdf endpoint that already renders amber section banners.
  const doDownloadPdf = async (s) => {
    if (!s?.id) return;
    try {
      const r = await axios.get(`${API}/vendor-submissions/${s.id}/preview-pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (s.title || 'submission').replace(/[^a-z0-9\-_]/gi, '_');
      a.download = `${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  // iter-313f: Convert an approved Quote/Estimate into a Running Bill — clones
  // items, marks them locked (vendor sees them readonly), and surfaces a
  // Copy Link + Email-to-Vendor dialog so admin can decide when to send.
  const [convertResult, setConvertResult] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const doConvertToBill = async (s) => {
    if (!window.confirm(`Convert "${s.title}" into a Running Bill?\n\nThe vendor will be able to fill Actual (Site) Qty/Rate and add new line items, but the original Quote items will be frozen.`)) return;
    try {
      const r = await axios.post(`${API}/vendor-submissions/${s.id}/convert-to-bill`);
      setConvertResult(r.data);
      setConvertOpen(true);
      toast.success('Running Bill created');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Convert failed');
    }
  };
  const doCopyBillLink = async () => {
    if (!convertResult) return;
    const url = convertResult.public_path
      ? `${window.location.origin}${convertResult.public_path}`
      : convertResult.public_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed — select the link manually');
    }
  };
  const doEmailBillToVendor = async () => {
    if (!convertResult?.submission_id) return;
    try {
      await axios.post(`${API}/vendor-submissions/${convertResult.submission_id}/email-bill-to-vendor`, {
        base_url: window.location.origin,
      });
      toast.success('Bill link emailed to vendor');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email failed');
    }
  };

  // iter-312q: Attach a standalone submission to a project.
  const openAttach = (s) => {
    setAttachTarget(s);
    setAttachProjectId('');
    setAttachOpen(true);
  };
  const submitAttach = async () => {
    if (!attachTarget || !attachProjectId) {
      toast.error('Select a project');
      return;
    }
    setAttachSaving(true);
    try {
      await axios.post(`${API}/vendor-submissions/${attachTarget.id}/attach-to-project`, { project_id: attachProjectId });
      toast.success('Attached to project');
      setAttachOpen(false);
      setAttachTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Attach failed');
    } finally {
      setAttachSaving(false);
    }
  };

  // iter-312t: link a vendor submission to ADDITIONAL projects (multi-select).
  const openLink = (s) => {
    setLinkTarget(s);
    setLinkSelected([]);
    setLinkOpen(true);
  };
  const submitLink = async () => {
    if (!linkTarget || linkSelected.length === 0) {
      toast.error('Select at least one project');
      return;
    }
    setLinkSaving(true);
    try {
      await axios.post(`${API}/vendor-submissions/${linkTarget.id}/link-projects`, { project_ids: linkSelected });
      toast.success(`Linked to ${linkSelected.length} additional project${linkSelected.length === 1 ? '' : 's'}`);
      setLinkOpen(false);
      setLinkTarget(null);
      setLinkSelected([]);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link');
    } finally {
      setLinkSaving(false);
    }
  };
  // iter-311: Add to BOQ helpers
  // iter-338e (Msg-680 round-5, opt 4): filter out archived / deleted /
  // legacy BOQs so vendors only see currently-active worksheets in the
  // "Add to existing BOQ" dropdown.
  const fetchProjectWorksheets = async (projectId) => {
    try {
      const r = await axios.get(`${API}/boq-worksheets`, { params: { project_id: projectId, active_filter: 'active' } });
      const live = (r.data || []).filter(w =>
        w.project_id === projectId
        && w.is_active !== false
        && !w.is_archived
        && (w.status || 'draft') !== 'deleted'
      );
      setProjectWorksheets(live);
    } catch {
      setProjectWorksheets([]);
    }
  };

  const submitAddToBOQ = async () => {
    if (!addBOQTarget) return;
    if (addBOQMode === 'existing' && !addBOQWorksheetId) {
      toast.error('Select a target worksheet');
      return;
    }
    try {
      setAddBOQSaving(true);
      const body = { mode: addBOQMode };
      if (addBOQMode === 'new') body.new_worksheet_name = addBOQName.trim() || `Vendor Bill — ${addBOQTarget.title}`;
      else body.worksheet_id = addBOQWorksheetId;
      const r = await axios.post(`${API}/vendor-submissions/${addBOQTarget.id}/add-to-boq`, body);
      toast.success(`Added ${r.data.items_added} item${r.data.items_added === 1 ? '' : 's'} to BOQ`);
      setAddBOQOpen(false);
      setAddBOQTarget(null);
      setAddBOQName('');
      setAddBOQWorksheetId('');
      setAddBOQMode('new');
      fetchAll();
      if (viewing?.id === addBOQTarget.id) setViewOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add to BOQ failed');
    } finally {
      setAddBOQSaving(false);
    }
  };

  const filtered = useMemo(() => {
    return submissions.filter(s => {
      if (filter.status && s.status !== filter.status) return false;
      if (filter.submission_type && s.submission_type !== filter.submission_type) return false;
      return true;
    });
  }, [submissions, filter]);

  return (
    <>
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 py-8'} data-testid="vendor-submissions-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            {!embedded && (
              <div className="flex items-center gap-2 mb-1">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                  <ArrowLeft size={14} className="mr-1" />Back
                </Button>
              </div>
            )}
            {!embedded && <h1 className="text-2xl font-bold text-stone-900">Vendor Submissions</h1>}
            <p className="text-sm text-stone-500">
              {isVendor ? 'Submit your quotes, estimates, and running bills.' : 'Review vendor-submitted quotes, estimates, and running bills.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => { setLinksOpen(o => !o); if (!shareLinks.length) fetchShareLinks(); }}
                data-testid="toggle-share-links-btn"
              >
                <Share2 size={14} className="mr-1" />Ad-hoc Vendor Links {linksOpen ? <ChevronUp size={12} className="ml-1" /> : <ChevronDown size={12} className="ml-1" />}
              </Button>
            )}
            {isVendor && (
              <Button onClick={openCreate} data-testid="new-vendor-submission-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus size={14} className="mr-1" />New Submission
              </Button>
            )}
          </div>
        </div>

        {/* iter-386u-k — Ad-hoc Submission Link (relocated from Vendor Dashboard) */}
        {isVendor && <VendorAdhocLinkCard projects={projects} />}

        {/* iter-312: Ad-hoc Vendor Share Links — admin only */}
        {isAdmin && linksOpen && (
          <Card className="mb-4 border-blue-200" data-testid="share-links-section">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
                    <LinkIcon size={14} className="text-blue-600" />Ad-hoc Vendor Submission Links
                  </h3>
                  <p className="text-xs text-stone-500">Send a secure link to a vendor who doesn't have an account — they fill the same BOQ-style form and submit. No signup required.</p>
                </div>
                <Button size="sm" onClick={() => setGenOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-share-link-btn">
                  <Plus size={12} className="mr-1" />Generate New Link
                </Button>
              </div>
              {linksLoading ? (
                <div className="p-6 text-center text-stone-500 text-xs">Loading…</div>
              ) : shareLinks.length === 0 ? (
                <div className="p-6 text-center text-stone-500 text-xs">No links yet. Click "Generate New Link" to create one.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-stone-50 text-stone-500">
                    <tr>
                      <th className="p-2 text-left">Type</th>
                      <th className="p-2 text-left">Project</th>
                      <th className="p-2 text-left">Note</th>
                      <th className="p-2 text-left">URL</th>
                      <th className="p-2 text-left">Used</th>
                      <th className="p-2 text-left">Last Activity</th>
                      <th className="p-2 text-left">Expires</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shareLinks.map(l => {
                      const expired = l.expires_at && new Date(l.expires_at) < new Date();
                      const isActive = !l.revoked && !expired;
                      const usedCount = l.used_count || 0;
                      return (
                        <tr key={l.id} className="border-t border-stone-100" data-testid={`share-link-row-${l.id}`}>
                          <td className="p-2 capitalize">{l.submission_type === 'bill' ? 'Running Bill' : l.submission_type}</td>
                          <td className="p-2">{l.project_name || <span className="italic text-stone-400">Standalone</span>}</td>
                          <td className="p-2 max-w-[220px] truncate" title={l.note || ''} data-testid={`share-link-note-${l.id}`}>
                            {l.note ? <span className="text-stone-700">{l.note}</span> : <span className="italic text-stone-300">—</span>}
                          </td>
                          <td className="p-2 max-w-[260px] truncate"><a className="text-blue-600 hover:underline" href={l.public_url} target="_blank" rel="noreferrer">{l.public_url}</a></td>
                          <td className="p-2">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${usedCount > 0 ? 'bg-blue-100 text-blue-700' : 'bg-stone-100 text-stone-500'}`}>
                              {usedCount}
                            </span>
                          </td>
                          <td className="p-2" data-testid={`share-link-last-activity-${l.id}`}>
                            {l.last_used_at ? (
                              <div className="flex flex-col leading-tight">
                                <span className="text-stone-700 text-[11px]" title={new Date(l.last_used_at).toLocaleString('en-IN')}>
                                  {new Date(l.last_used_at).toLocaleDateString('en-IN')}
                                </span>
                                {l.last_submitter_name && (
                                  <span className="text-stone-500 text-[10px] truncate max-w-[140px]" title={`${l.last_submitter_name} <${l.last_submitter_email || ''}>`}>
                                    by {l.last_submitter_name}
                                  </span>
                                )}
                              </div>
                            ) : l.last_emailed_at ? (
                              <div className="flex flex-col leading-tight">
                                <span className="text-amber-700 text-[11px]" title={new Date(l.last_emailed_at).toLocaleString('en-IN')}>
                                  Sent {new Date(l.last_emailed_at).toLocaleDateString('en-IN')}
                                </span>
                                <span className="text-stone-500 text-[10px] truncate max-w-[140px]" title={l.last_emailed_to}>
                                  to {l.last_emailed_name || l.last_emailed_to}{(l.email_count || 0) > 1 ? ` · ${l.email_count}×` : ''}
                                </span>
                              </div>
                            ) : (
                              <span className="italic text-stone-300">—</span>
                            )}
                          </td>
                          <td className="p-2">{l.expires_at ? new Date(l.expires_at).toLocaleDateString('en-IN') : '—'}</td>
                          <td className="p-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${expired ? 'bg-stone-200 text-stone-600' : 'bg-green-100 text-green-700'}`}>
                              {expired ? 'Expired' : 'Active'}
                            </span>
                          </td>
                          <td className="p-2 text-right">
                            <div className="inline-flex items-center gap-1">
                              {isActive && (
                                <>
                                  <button onClick={() => copyToClipboard(l.public_url)} className="w-7 h-7 inline-flex items-center justify-center rounded text-stone-600 hover:bg-stone-100" title="Copy URL" data-testid={`copy-link-${l.id}`}>
                                    <Copy size={13} />
                                  </button>
                                  <button onClick={() => openLinkEmail(l)} className="w-7 h-7 inline-flex items-center justify-center rounded text-blue-600 hover:bg-blue-50" title="Email link" data-testid={`email-link-${l.id}`}>
                                    <Mail size={13} />
                                  </button>
                                  {usedCount === 0 && l.last_emailed_to && (
                                    <button
                                      onClick={() => resendShareLink(l)}
                                      disabled={resendingId === l.id}
                                      className="w-7 h-7 inline-flex items-center justify-center rounded text-amber-600 hover:bg-amber-50 disabled:opacity-50"
                                      title={`Resend reminder to ${l.last_emailed_to}${l.email_count > 1 ? ` (sent ${l.email_count}×)` : ''}`}
                                      data-testid={`resend-link-${l.id}`}
                                    >
                                      <Send size={13} />
                                    </button>
                                  )}
                                </>
                              )}
                              <button onClick={() => deleteShareLink(l.id)} className="w-7 h-7 inline-flex items-center justify-center rounded text-red-500 hover:bg-red-50" title="Delete link" data-testid={`delete-link-${l.id}`}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Label className="text-xs text-stone-500">Type:</Label>
          <select value={filter.submission_type} onChange={e => setFilter({ ...filter, submission_type: e.target.value })} className="h-8 text-sm border border-stone-300 rounded px-2" data-testid="filter-type">
            <option value="">All</option>
            {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <Label className="text-xs text-stone-500 ml-3">Status:</Label>
          <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} className="h-8 text-sm border border-stone-300 rounded px-2" data-testid="filter-status">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center text-stone-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center text-stone-500">
                No submissions yet.
                {isVendor && <span className="block text-xs mt-1">Click "New Submission" to create your first one.</span>}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200 text-xs uppercase tracking-wide text-stone-500">
                  <tr>
                    {/* iter-386u-p — canonical headers per spec:
                        TITLE, TYPE, PROJECT, TOTAL, STATUS, CREATED, ACTIONS.
                        Vendor column stays for admins so they can identify
                        who submitted a given row. */}
                    <th className="p-3 text-left">Title</th>
                    <th className="p-3 text-left">Type</th>
                    {isAdmin && <th className="p-3 text-left">Vendor</th>}
                    <th className="p-3 text-left">Project</th>
                    <th className="p-3 text-right">Total</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Created</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => (
                    <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`sub-row-${s.id}`}>
                      <td className="p-3">
                        <button className="font-semibold text-blue-700 hover:underline text-left" onClick={() => { setViewing(s); setViewOpen(true); }}>
                          {s.title}
                        </button>
                        {/* iter-315d: Running Bill status chip — only shown on bill rows */}
                        {s.submission_type === 'bill' && s.bill_status_meta && (
                          <RunningBillStatusBadge meta={s.bill_status_meta} testId={`bill-status-${s.id}`} />
                        )}
                      </td>
                      <td className="p-3 capitalize">{s.submission_type === 'bill' ? 'Running Bill' : s.submission_type}</td>
                      {isAdmin && (
                        <td className="p-3 text-stone-700">
                          {s.vendor_name}
                          {s.is_adhoc && (
                            <span className="ml-1.5 inline-block px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-semibold uppercase" title="Submitted via ad-hoc share link" data-testid={`adhoc-badge-${s.id}`}>Ad-hoc</span>
                          )}
                        </td>
                      )}
                      <td className="p-3 text-stone-500 text-xs">{s.project_name || <span className="italic">Standalone</span>}</td>
                      <td className="p-3 text-right font-semibold">{formatINR(s.grand_total)}</td>
                      <td className="p-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${STATUS_COLORS[s.status] || ''}`} data-testid={`sub-status-${s.id}`}>{s.status}</span>
                      </td>
                      <td className="p-3 text-stone-500 text-xs">{new Date(s.created_at).toLocaleDateString('en-IN')}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => { setViewing(s); setViewOpen(true); }} className="w-7 h-7 inline-flex items-center justify-center rounded text-stone-600 hover:bg-stone-100" title="View" data-testid={`sub-view-${s.id}`}>
                            <Eye size={14} />
                          </button>
                          {isAdmin && !s.project_id && (
                            <button onClick={() => openAttach(s)} className="w-7 h-7 inline-flex items-center justify-center rounded text-emerald-700 hover:bg-emerald-50" title="Attach to a project" data-testid={`sub-attach-${s.id}`}>
                              <Folder size={14} />
                            </button>
                          )}
                          {isAdmin && s.project_id && (
                            <button onClick={() => openLink(s)} className="w-7 h-7 inline-flex items-center justify-center rounded text-blue-700 hover:bg-blue-50" title={`Apply this quote to additional projects${(s.linked_project_ids||[]).length ? ` (currently linked to ${(s.linked_project_ids||[]).length})` : ''}`} data-testid={`sub-link-${s.id}`}>
                              <Share2 size={14} />
                            </button>
                          )}
                          {/* iter-313f: Allow Edit even for approved/rejected quotes — admin only, with confirm */}
                          {s.vendor_id === user.id && s.status !== 'approved' && s.status !== 'rejected' && (
                            <button onClick={() => openEdit(s)} className="w-7 h-7 inline-flex items-center justify-center rounded text-blue-600 hover:bg-blue-50" title="Edit" data-testid={`sub-edit-${s.id}`}>
                              <FileText size={13} />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => {
                                if (s.status === 'approved' || s.status === 'rejected') {
                                  if (!window.confirm(`This ${s.submission_type} is ${s.status} — editing will not auto-update related downstream docs (PDFs, emails, running bills). Continue?`)) return;
                                }
                                openEdit(s);
                              }}
                              className="w-7 h-7 inline-flex items-center justify-center rounded text-blue-600 hover:bg-blue-50"
                              title={s.status === 'approved' ? 'Edit (approved — confirms downstream impact)' : 'Edit'}
                              data-testid={`sub-edit-${s.id}`}
                            >
                              <FileText size={13} />
                            </button>
                          )}
                          {/* iter-386u-p: Submit RA Bill — visible to Admin
                              OR the OWNING vendor once submission is approved. */}
                          {(isAdmin || s.vendor_id === user.id) && s.status === 'approved' && (s.submission_type === 'quote' || s.submission_type === 'estimate') && (
                            <button
                              onClick={() => doConvertToBill(s)}
                              className="w-7 h-7 inline-flex items-center justify-center rounded text-emerald-700 hover:bg-emerald-50"
                              title="Submit RA Bill (Running Bill) — fill site qty/rate"
                              data-testid={`sub-convert-bill-${s.id}`}
                            >
                              <Receipt size={14} />
                            </button>
                          )}
                          {isAdmin && s.status === 'submitted' && (
                            <>
                              <button onClick={() => doApprove(s)} className="w-7 h-7 inline-flex items-center justify-center rounded text-green-700 hover:bg-green-50" title="Approve" data-testid={`sub-approve-${s.id}`}>
                                <Check size={14} />
                              </button>
                              <button onClick={() => doReject(s)} className="w-7 h-7 inline-flex items-center justify-center rounded text-red-700 hover:bg-red-50" title="Reject" data-testid={`sub-reject-${s.id}`}>
                                <X size={14} />
                              </button>
                            </>
                          )}
                          {(isAdmin || (s.vendor_id === user.id && (s.status === 'draft' || s.status === 'submitted'))) && (
                            <button onClick={() => doDelete(s)} className="w-7 h-7 inline-flex items-center justify-center rounded text-red-500 hover:bg-red-50" title="Delete" data-testid={`sub-delete-${s.id}`}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Create / Edit Modal */}
        <Dialog open={formOpen} onOpenChange={(o) => !o && setFormOpen(false)}>
          {/* iter-338f (Msg-680 round-7): removed inline zoom directive which
             was making fonts/inputs look oversized on edit — now matches the
             rest of the app (Edit BOQ etc.). */}
          <DialogContent className="!w-screen !h-screen !max-w-none p-3 sm:p-4 overflow-hidden flex flex-col !rounded-none !top-0 !left-0 !translate-x-0 !translate-y-0 !inset-0" data-testid="vendor-submission-form">
            {/* iter-338h (Msg-680 round-8): font-size:80% wrapper. Replaces
               the previous `zoom: 1.25` which also scaled the modal up so it
               felt oversized. Wrapping the inner content in a 0.8rem-based
               style keeps the dialog at full screen but visually shrinks all
               text + inputs uniformly. */}
            <div className="flex flex-col flex-1 overflow-hidden" style={{ fontSize: '80%' }}>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit' : 'New'} Vendor Submission</DialogTitle>
              <DialogDescription className="text-xs text-stone-500">
                Add multiple line items. Main Category &amp; Task title are required. Project association is optional.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={(e) => handleSubmit(e, 'submitted')} className="flex-1 overflow-auto space-y-4 px-1">
              {/* Top fields */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Type *</Label>
                  <select
                    value={form.submission_type}
                    onChange={e => setForm({ ...form, submission_type: e.target.value })}
                    className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm"
                    data-testid="form-submission-type"
                  >
                    {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Title *</Label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., False-ceiling quote for Anika villa" data-testid="form-title" required />
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-xs">Project (optional)</Label>
                  <select
                    value={form.project_id}
                    onChange={e => setForm({ ...form, project_id: e.target.value })}
                    className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm"
                    data-testid="form-project"
                  >
                    <option value="">— Standalone (no project) —</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Line items — BOQ Edit style table */}
              <div className="border border-stone-200 rounded-md overflow-hidden">
                <div className="bg-stone-50 px-3 py-2 flex items-center justify-between gap-2 border-b border-stone-200 flex-wrap">
                  <span className="text-sm font-semibold text-stone-700">Line Items ({form.items.filter(i => !(i.item_type === 'header' || i.is_header)).length})</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button type="button" size="sm" variant="outline" onClick={addHeader} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="modal-add-header-btn">
                      <LayoutDashboard size={12} className="mr-1" />Add Header
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-line-item-btn">
                      <Plus size={12} className="mr-1" />Add Item
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed' }} data-testid="vendor-sub-items-table">
                    <colgroup>
                      <col style={{ width: 44 }} />
                      <col style={{ width: 340 }} />
                      <col style={{ width: 76 }} />
                      <col style={{ width: 70 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 76 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 70 }} />
                      <col style={{ width: 180 }} />
                      <col style={{ width: 40 }} />
                    </colgroup>
                    <thead className="bg-stone-50 text-stone-600">
                      {/* Group header row */}
                      <tr className="border-b-2 border-stone-200">
                        <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">SR</th>
                        <th rowSpan={2} className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">Description</th>
                        <th colSpan={4} className="px-2 py-2 text-center text-[11px] font-bold bg-blue-50 text-blue-800 border-r border-stone-200 border-b border-blue-100 uppercase tracking-wide">
                          Estimated (Design)
                        </th>
                        <th colSpan={3} className="px-2 py-2 text-center text-[11px] font-bold bg-green-50 text-green-800 border-r border-stone-200 border-b border-green-100 uppercase tracking-wide">
                          Actual (Site)
                        </th>
                        <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200 bg-amber-50 text-amber-700">Variance</th>
                        <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">GST%</th>
                        <th rowSpan={2} className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">Remarks</th>
                        <th rowSpan={2} className="px-2 py-2 text-center text-[11px]"><Trash2 size={11} /></th>
                      </tr>
                      {/* Sub-header row */}
                      <tr>
                        <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 uppercase tracking-wide">Qty</th>
                        <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 uppercase tracking-wide">Unit</th>
                        <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 uppercase tracking-wide">Rate</th>
                        <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 border-r border-stone-200 uppercase tracking-wide">Amt</th>
                        <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 uppercase tracking-wide">Qty</th>
                        <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 uppercase tracking-wide">Rate</th>
                        <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 border-r border-stone-200 uppercase tracking-wide">Amt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, idx) => {
                        // iter-315c: amber section-header banner — matches Edit BOQ + Public Vendor Submit
                        if (it.item_type === 'header' || it.is_header) {
                          const isDragOver = dragOverIdx === idx;
                          return (
                            <tr
                              key={idx}
                              className={`bg-amber-100 border-y-2 border-amber-400 ${isDragOver ? 'outline outline-2 outline-blue-500' : ''}`}
                              data-testid={`modal-header-row-${idx}`}
                              draggable
                              onDragStart={handleDragStart(idx)}
                              onDragOver={handleDragOver(idx)}
                              onDrop={handleDrop(idx)}
                              onDragEnd={handleDragEnd}
                            >
                              <td className="px-2 py-2 text-center text-amber-800 font-bold">
                                <div className="flex items-center justify-center gap-1">
                                  <GripVertical size={12} className="text-amber-700/70 cursor-grab active:cursor-grabbing" title="Drag to reorder" />
                                  <span>{idx + 1}</span>
                                </div>
                              </td>
                              <td colSpan={11} className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <LayoutDashboard size={14} className="text-amber-800 shrink-0" />
                                  <input
                                    value={it.task_title || ''}
                                    onChange={e => updateItem(idx, 'task_title', e.target.value)}
                                    placeholder="Section title (e.g. Material, Labour, Site Extras)"
                                    className="flex-1 bg-transparent border-0 outline-none text-sm font-bold text-amber-900 uppercase tracking-wide placeholder:text-amber-400 placeholder:font-normal placeholder:normal-case"
                                    data-testid={`modal-header-${idx}-title`}
                                  />
                                </div>
                              </td>
                              <td className="p-1 text-center">
                                <button type="button" onClick={() => removeItem(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Remove header">
                                  <Trash2 size={12} />
                                </button>
                              </td>
                            </tr>
                          );
                        }
                        const eq = +it.quantity || 0;
                        const er = +it.rate || 0;
                        const estAmt = eq * er;
                        const aq = it.actual_quantity != null ? +it.actual_quantity : eq;
                        const ar = it.actual_rate != null ? +it.actual_rate : er;
                        const actAmt = aq * ar;
                        const variance = actAmt - estAmt;
                        const subs = subByMain[it.main_category_id] || [];
                        const descKey = `d_${idx}`;
                        const remKey = `r_${idx}`;
                        const isDescExpanded = expandedDescs.has(descKey);
                        const isRemExpanded = expandedRemarks.has(remKey);
                        const descText = it.description || '';
                        const remarksText = it.notes || '';
                        return (
                          <tr
                            key={idx}
                            className={`border-b border-stone-200 hover:bg-stone-50/60 align-top ${dragOverIdx === idx ? 'outline outline-2 outline-blue-500' : ''}`}
                            data-testid={`line-item-${idx}`}
                            draggable
                            onDragStart={handleDragStart(idx)}
                            onDragOver={handleDragOver(idx)}
                            onDrop={handleDrop(idx)}
                            onDragEnd={handleDragEnd}
                          >
                            {/* SR */}
                            <td className="px-2 py-3 text-center text-stone-500">
                              <div className="flex items-center justify-center gap-1">
                                <GripVertical size={12} className="text-stone-400 cursor-grab active:cursor-grabbing" title="Drag to reorder" />
                                <span>{idx + 1}</span>
                              </div>
                            </td>

                            {/* Description cell: title + category combo + expandable description */}
                            <td className="px-2 py-2 border-r border-stone-100">
                              <div className="space-y-1.5">
                                {/* iter-312n: wrap long titles by switching to an auto-growing textarea */}
                                <textarea
                                  value={it.task_title}
                                  onChange={e => updateItem(idx, 'task_title', e.target.value)}
                                  onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                  ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                                  rows={1}
                                  placeholder="Item title"
                                  className="w-full text-sm font-semibold text-stone-900 border-0 bg-transparent px-1 py-1 outline-none resize-none whitespace-pre-wrap break-words leading-snug focus:bg-white focus:ring-1 focus:ring-blue-200 rounded"
                                  required
                                  data-testid={`item-${idx}-task-title`}
                                />
                                <div className="flex items-center gap-1.5 bg-stone-100 rounded-md px-1.5 py-1 border border-stone-300">
                                  <select
                                    value={it.main_category_id}
                                    onChange={e => updateItem(idx, 'main_category_id', e.target.value)}
                                    className="flex-1 h-7 text-xs bg-transparent border-0 outline-none text-stone-700"
                                    required
                                    data-testid={`item-${idx}-main-category`}
                                  >
                                    <option value="">+ Category</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.code ? `${c.code} — ` : ''}{c.name}</option>)}
                                  </select>
                                  {it.main_category_id && subs.length > 0 && (
                                    <>
                                      <span className="text-stone-400 font-bold">/</span>
                                      <select
                                        value={it.sub_category_id}
                                        onChange={e => updateItem(idx, 'sub_category_id', e.target.value)}
                                        className="flex-1 h-7 text-xs bg-transparent border-0 outline-none text-stone-700"
                                        data-testid={`item-${idx}-sub-category`}
                                      >
                                        <option value="">Sub</option>
                                        {subs.map(s => <option key={s.id} value={s.id}>{s.code ? `${s.code} — ` : ''}{s.name}</option>)}
                                      </select>
                                    </>
                                  )}
                                </div>
                                {isDescExpanded ? (
                                  <div className="relative">
                                    <Textarea
                                      value={descText}
                                      onChange={e => updateItem(idx, 'description', e.target.value)}
                                      className="text-xs text-stone-600 border border-blue-200 bg-blue-50/30 rounded resize-y"
                                      style={{ minHeight: '60px' }}
                                      placeholder="Description / scope detail"
                                      data-testid={`item-${idx}-description`}
                                    />
                                    <button type="button" onClick={() => toggleDesc(descKey)} className="absolute top-1 right-1 p-0.5 bg-stone-200/80 hover:bg-stone-300 rounded text-stone-500">
                                      <ChevronUp size={12} />
                                    </button>
                                  </div>
                                ) : (
                                  <div
                                    className="relative cursor-pointer group/desc"
                                    onClick={() => toggleDesc(descKey)}
                                    title={descText.length > 60 ? 'Click to expand' : 'Click to add description'}
                                  >
                                    <div className="text-xs text-stone-500 leading-relaxed line-clamp-2 pr-4">
                                      {descText || <span className="text-stone-300 italic">Click to add description</span>}
                                    </div>
                                    {descText.length > 60 && <ChevronDown size={12} className="absolute right-0 top-1 text-stone-400" />}
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Estimated columns (blue) */}
                            <td className="px-1 py-1 bg-blue-50/20 border-l border-blue-100">
                              <Input type="number" min="0" step="0.01" value={eq}
                                onChange={e => updateItem(idx, 'quantity', e.target.value === '' ? 0 : +e.target.value)}
                                className="h-9 w-full text-center text-sm text-blue-700 border-0 bg-transparent px-1"
                                data-testid={`item-${idx}-qty`} />
                            </td>
                            <td className="px-1 py-1 bg-blue-50/20">
                              <select value={it.unit} onChange={e => updateItem(idx, 'unit', e.target.value)}
                                className="h-9 w-full text-xs text-stone-600 border-0 bg-transparent"
                                data-testid={`item-${idx}-unit`}>
                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </td>
                            <td className="px-1 py-1 bg-blue-50/20">
                              <Input type="number" min="0" step="0.01" value={er}
                                onChange={e => updateItem(idx, 'rate', e.target.value === '' ? 0 : +e.target.value)}
                                className="h-9 w-full text-right text-sm text-stone-700 border-0 bg-transparent px-1"
                                data-testid={`item-${idx}-rate`} />
                            </td>
                            <td className="px-2 py-3 text-right font-semibold text-blue-700 bg-blue-50/30 text-sm whitespace-nowrap border-r border-stone-100">
                              {formatINR(estAmt)}
                            </td>

                            {/* Actual columns (green) */}
                            <td className="px-1 py-1 bg-green-50/20 border-l border-green-100">
                              <Input type="number" min="0" step="0.01"
                                value={it.actual_quantity ?? ''}
                                onChange={e => updateItem(idx, 'actual_quantity', e.target.value === '' ? null : +e.target.value)}
                                placeholder={`${eq}`}
                                className="h-9 w-full text-center text-sm text-green-700 border-0 bg-transparent px-1"
                                data-testid={`item-${idx}-actual-qty`} />
                            </td>
                            <td className="px-1 py-1 bg-green-50/20">
                              <Input type="number" min="0" step="0.01"
                                value={it.actual_rate ?? ''}
                                onChange={e => updateItem(idx, 'actual_rate', e.target.value === '' ? null : +e.target.value)}
                                placeholder={`${er}`}
                                className="h-9 w-full text-right text-sm text-stone-700 border-0 bg-transparent px-1"
                                data-testid={`item-${idx}-actual-rate`} />
                            </td>
                            <td className="px-2 py-3 text-right font-semibold text-green-700 bg-green-50/30 text-sm whitespace-nowrap border-r border-stone-100">
                              {formatINR(actAmt)}
                            </td>

                            {/* Variance */}
                            <td className={`px-2 py-3 text-right text-sm font-medium whitespace-nowrap border-r border-stone-100 ${variance > 0 ? 'text-red-600 bg-red-50/30' : variance < 0 ? 'text-green-600 bg-green-50/20' : 'text-stone-400'}`} data-testid={`item-${idx}-variance`}>
                              {variance !== 0 ? (variance > 0 ? '+' : '') + formatINR(variance) : '-'}
                            </td>

                            {/* GST% */}
                            <td className="px-0 py-1 border-r border-stone-100">
                              <select
                                value={it.gst_percentage}
                                onChange={e => updateItem(idx, 'gst_percentage', +e.target.value)}
                                className="h-9 w-full text-center text-sm text-stone-900 border-0 bg-transparent"
                                data-testid={`item-${idx}-gst`}
                              >
                                {GST_OPTIONS.map(g => <option key={g} value={g}>{g}%</option>)}
                              </select>
                            </td>

                            {/* Remarks */}
                            <td className="px-2 py-2 border-r border-stone-100">
                              {isRemExpanded ? (
                                <div className="relative">
                                  <Textarea
                                    value={remarksText}
                                    onChange={e => updateItem(idx, 'notes', e.target.value)}
                                    className="text-xs text-stone-600 border border-blue-200 bg-blue-50/30 rounded resize-y"
                                    style={{ minHeight: '60px' }}
                                    placeholder="Add remarks..."
                                    data-testid={`item-${idx}-remarks`}
                                  />
                                  <button type="button" onClick={() => toggleRemarks(remKey)} className="absolute top-1 right-1 p-0.5 bg-stone-200/80 hover:bg-stone-300 rounded text-stone-500">
                                    <ChevronUp size={12} />
                                  </button>
                                </div>
                              ) : (
                                <div
                                  className="relative cursor-pointer group/remarks"
                                  onClick={() => toggleRemarks(remKey)}
                                  title={remarksText.length > 60 ? 'Click to expand' : 'Click to add remarks'}
                                >
                                  <div className="text-xs text-stone-600 leading-relaxed line-clamp-3 pr-4">
                                    {remarksText || <span className="text-stone-300 italic">Click to add remarks</span>}
                                  </div>
                                  {remarksText.length > 60 && <ChevronDown size={12} className="absolute right-0 top-1 text-stone-400" />}
                                </div>
                              )}
                            </td>

                            {/* Delete */}
                            <td className="p-1 text-center">
                              {form.items.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeItem(idx)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  title="Remove line item"
                                  data-testid={`remove-line-item-${idx}`}
                                >
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top-level notes */}
              <div>
                <Label className="text-xs">Notes (overall)</Label>
                <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional context about this submission..." data-testid="form-notes" />
              </div>

              {/* Totals + actions */}
              <div className="sticky bottom-0 -mx-1 bg-white border-t border-stone-200 pt-3 pb-1 mt-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-3">
                  <div><span className="text-stone-500 text-xs">Est Total</span><div className="font-semibold">{formatINR(totals.total)}</div></div>
                  <div><span className="text-stone-500 text-xs">Actual Total</span><div className="font-semibold text-green-800" data-testid="form-actual-total">{formatINR(totals.actTotal)}</div></div>
                  <div>
                    <span className="text-stone-500 text-xs">Variance</span>
                    <div className={`font-semibold ${totals.variance > 0 ? 'text-red-700' : totals.variance < 0 ? 'text-green-700' : 'text-stone-500'}`} data-testid="form-variance">
                      {totals.variance > 0 ? '+' : ''}{formatINR(totals.variance)}
                    </div>
                  </div>
                  <div><span className="text-stone-500 text-xs">GST (Est / Act)</span><div className="font-semibold text-xs">{formatINR(totals.gst)} / {formatINR(totals.aGst)}</div></div>
                </div>
                <div className="text-[11px] text-stone-500 mb-3" data-testid="form-grand-total">Grand Total (Actual): {formatINR(totals.actTotal)}</div>
                <div className="flex justify-end gap-2 flex-wrap">
                  <Button type="button" variant="outline" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                  {editingId && (
                    <Button
                      type="button" variant="outline"
                      onClick={() => openEmailPreview({ ...form, id: editingId, vendor_name: user?.name || user?.email, project_name: projects.find(p => p.id === form.project_id)?.name, submission_type: form.submission_type, title: form.title })}
                      disabled={saving}
                      data-testid="form-email-preview-btn"
                    >
                      <Mail size={12} className="mr-1" />Email Preview
                    </Button>
                  )}
                  {/* iter-338 (Msg-680): "Save as Draft" must be available on
                     any vendor submission status — including submitted and
                     approved — so the admin/vendor can roll the bill back
                     to draft state for re-edits. Backend allows status='draft'
                     overrides when the caller is owner/admin. */}
                  {(!editingId || (editingId && (isVendor || isAdmin))) && (
                    <Button type="button" variant="outline" onClick={(e) => handleSubmit(e, 'draft')} disabled={saving} data-testid="save-draft-btn">
                      Save as Draft
                    </Button>
                  )}
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving} data-testid="submit-btn">
                    {saving ? 'Saving…' : (editingId ? 'Save & Send for Approval' : 'Send for Approval')}
                  </Button>
                </div>
              </div>
            </form>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Modal */}
        <Dialog open={viewOpen} onOpenChange={(o) => !o && setViewOpen(false)}>
          <DialogContent className="!w-screen !h-screen !max-w-none p-3 sm:p-4 overflow-hidden flex flex-col !rounded-none !top-0 !left-0 !translate-x-0 !translate-y-0 !inset-0" data-testid="vendor-submission-view">
            {/* iter-338h: same font-size:80% wrapper as the form modal — keeps
               the dialog full-screen but shrinks text uniformly. */}
            <div className="flex flex-col flex-1 overflow-hidden" style={{ fontSize: '80%' }}>
            {viewing && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span>{viewing.title}</span>
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${STATUS_COLORS[viewing.status] || ''}`}>{viewing.status}</span>
                  </DialogTitle>
                  <DialogDescription className="text-xs text-stone-500">
                    {viewing.submission_type === 'bill' ? 'Running Bill' : (viewing.submission_type[0].toUpperCase() + viewing.submission_type.slice(1))} by <strong>{viewing.vendor_name}</strong>
                    {viewing.project_name ? <> · Project: <strong>{viewing.project_name}</strong></> : ' · Standalone'}
                  </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-auto">
                  {/* BOQ-style read-only table — matches the Create/Edit dialog layout exactly */}
                  <div className="border border-stone-200 rounded-md overflow-hidden">
                    <div className="bg-stone-50 px-3 py-2 flex items-center justify-between border-b border-stone-200">
                      <span className="text-sm font-semibold text-stone-700">
                        Line Items ({(viewing.items || []).filter(i => !(i.item_type === 'header' || i.is_header)).length})
                      </span>
                      <div className="flex items-center gap-2 flex-wrap">
                        {isAdmin && viewing.is_adhoc && (
                          <Button
                            type="button" size="sm" variant="outline"
                            className="border-amber-300 text-amber-800 hover:bg-amber-50"
                            onClick={() => doConvertAdhoc(viewing)}
                            disabled={convertSaving}
                            data-testid="convert-to-vendor-btn"
                          >
                            {convertSaving ? 'Converting…' : 'Convert to Registered Vendor'}
                          </Button>
                        )}
                        {/* iter-315d: Convert to PDF directly from the View modal */}
                        <Button
                          type="button" size="sm" variant="outline"
                          onClick={() => doDownloadPdf(viewing)}
                          data-testid="view-download-pdf-btn"
                          className="border-rose-300 text-rose-700 hover:bg-rose-50"
                        >
                          <FileText size={12} className="mr-1" />Convert to PDF
                        </Button>
                        <Button
                          type="button" size="sm" variant="outline"
                          onClick={() => openEmailPreview(viewing)}
                          data-testid="view-email-preview-btn"
                          disabled={viewing.status === 'draft'}
                        >
                          <Mail size={12} className="mr-1" />Email Preview
                        </Button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed' }} data-testid="vendor-sub-view-table">
                        <colgroup>
                          <col style={{ width: 44 }} />
                          <col style={{ width: 340 }} />
                          <col style={{ width: 76 }} />
                          <col style={{ width: 70 }} />
                          <col style={{ width: 100 }} />
                          <col style={{ width: 110 }} />
                          <col style={{ width: 76 }} />
                          <col style={{ width: 100 }} />
                          <col style={{ width: 110 }} />
                          <col style={{ width: 100 }} />
                          <col style={{ width: 70 }} />
                          <col style={{ width: 180 }} />
                        </colgroup>
                        <thead className="bg-stone-50 text-stone-600">
                          <tr className="border-b-2 border-stone-200">
                            <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">SR</th>
                            <th rowSpan={2} className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">Description</th>
                            <th colSpan={4} className="px-2 py-2 text-center text-[11px] font-bold bg-blue-50 text-blue-800 border-r border-stone-200 border-b border-blue-100 uppercase tracking-wide">Estimated (Design)</th>
                            <th colSpan={3} className="px-2 py-2 text-center text-[11px] font-bold bg-green-50 text-green-800 border-r border-stone-200 border-b border-green-100 uppercase tracking-wide">Actual (Site)</th>
                            <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200 bg-amber-50 text-amber-700">Variance</th>
                            <th rowSpan={2} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide border-r border-stone-200">GST%</th>
                            <th rowSpan={2} className="px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide">Remarks</th>
                          </tr>
                          <tr>
                            <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 uppercase tracking-wide">Qty</th>
                            <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 uppercase tracking-wide">Unit</th>
                            <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 uppercase tracking-wide">Rate</th>
                            <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-blue-50/60 text-blue-700 border-r border-stone-200 uppercase tracking-wide">Amt</th>
                            <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 uppercase tracking-wide">Qty</th>
                            <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 uppercase tracking-wide">Rate</th>
                            <th className="px-1 py-1.5 text-center text-[10px] font-semibold bg-green-50/60 text-green-700 border-r border-stone-200 uppercase tracking-wide">Amt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(viewing.items || []).map((it, idx) => {
                            // iter-315d: render amber section banner for header rows
                            if (it.item_type === 'header' || it.is_header) {
                              return (
                                <tr key={idx} className="bg-amber-100 border-y-2 border-amber-400" data-testid={`view-header-row-${idx}`}>
                                  <td className="px-2 py-2 text-center text-amber-800 font-bold">{idx + 1}</td>
                                  <td colSpan={11} className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <LayoutDashboard size={14} className="text-amber-800 shrink-0" />
                                      <span className="text-sm font-bold text-amber-900 uppercase tracking-wide">{it.task_title || ''}</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            }
                            const eq = +it.quantity || 0;
                            const er = +it.rate || 0;
                            const eAmt = eq * er;
                            const aq = it.actual_quantity != null ? +it.actual_quantity : eq;
                            const ar = it.actual_rate != null ? +it.actual_rate : er;
                            const aAmt = aq * ar;
                            const v = aAmt - eAmt;
                            return (
                              <tr key={it.id || idx} className="border-b border-stone-200 align-top">
                                <td className="px-2 py-3 text-center text-stone-500">{idx + 1}</td>
                                <td className="px-2 py-2 border-r border-stone-100">
                                  <div className="space-y-1">
                                    <div className="text-sm font-semibold text-stone-900">{it.task_title}</div>
                                    {(it.main_category_name || it.sub_category_name) && (
                                      <div className="text-[11px] text-stone-500">
                                        {it.main_category_name}{it.sub_category_name ? ` › ${it.sub_category_name}` : ''}
                                      </div>
                                    )}
                                    {it.description && (
                                      <div className="text-xs text-stone-500 leading-relaxed whitespace-pre-wrap">{it.description}</div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-3 text-center text-blue-700 bg-blue-50/20 text-sm">{eq}</td>
                                <td className="px-2 py-3 text-center text-stone-600 bg-blue-50/20 text-sm">{it.unit}</td>
                                <td className="px-2 py-3 text-right text-stone-700 bg-blue-50/20 text-sm">{formatINR(er)}</td>
                                <td className="px-2 py-3 text-right font-semibold text-blue-700 bg-blue-50/30 text-sm border-r border-stone-100 whitespace-nowrap">{formatINR(eAmt)}</td>
                                <td className="px-2 py-3 text-center text-green-700 bg-green-50/20 text-sm">{aq}</td>
                                <td className="px-2 py-3 text-right text-stone-700 bg-green-50/20 text-sm">{formatINR(ar)}</td>
                                <td className="px-2 py-3 text-right font-semibold text-green-700 bg-green-50/30 text-sm border-r border-stone-100 whitespace-nowrap">{formatINR(aAmt)}</td>
                                <td className={`px-2 py-3 text-right text-sm font-medium border-r border-stone-100 whitespace-nowrap ${v > 0 ? 'text-red-600 bg-red-50/30' : v < 0 ? 'text-green-600 bg-green-50/20' : 'text-stone-400'}`}>
                                  {v !== 0 ? (v > 0 ? '+' : '') + formatINR(v) : '-'}
                                </td>
                                <td className="px-2 py-3 text-center text-stone-700 text-sm border-r border-stone-100">{it.gst_percentage || 0}%</td>
                                <td className="px-2 py-2 text-xs text-stone-600 leading-relaxed whitespace-pre-wrap">
                                  {it.notes || <span className="text-stone-300 italic">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-stone-50">
                          <tr><td colSpan="5" className="px-3 py-2 text-right text-stone-500 text-xs">Estimated Subtotal</td><td className="px-2 py-2 text-right font-semibold text-blue-700 bg-blue-50/30 border-r border-stone-100">{formatINR(viewing.subtotal)}</td><td colSpan="2"></td><td className="px-2 py-2 text-right font-semibold text-green-700 bg-green-50/30 border-r border-stone-100">{formatINR(viewing.actual_subtotal || viewing.subtotal)}</td><td colSpan="3"></td></tr>
                          {(viewing.discount_amount > 0 || viewing.actual_discount_amount > 0) && (
                            <tr><td colSpan="5" className="px-3 py-2 text-right text-stone-500 text-xs">Discount</td><td className="px-2 py-2 text-right text-amber-700 border-r border-stone-100">- {formatINR(viewing.discount_amount)}</td><td colSpan="2"></td><td className="px-2 py-2 text-right text-amber-700 border-r border-stone-100">- {formatINR(viewing.actual_discount_amount || viewing.discount_amount)}</td><td colSpan="3"></td></tr>
                          )}
                          <tr><td colSpan="5" className="px-3 py-2 text-right text-stone-500 text-xs">GST</td><td className="px-2 py-2 text-right border-r border-stone-100">+ {formatINR(viewing.gst_amount)}</td><td colSpan="2"></td><td className="px-2 py-2 text-right border-r border-stone-100">+ {formatINR(viewing.actual_gst_amount || viewing.gst_amount)}</td><td colSpan="3"></td></tr>
                          <tr className="font-bold border-t-2 border-stone-300">
                            <td colSpan="5" className="px-3 py-2 text-right">Grand Total</td>
                            <td className="px-2 py-2 text-right bg-blue-100 text-blue-900 border-r border-stone-100">{formatINR(viewing.grand_total)}</td>
                            <td colSpan="2"></td>
                            <td className="px-2 py-2 text-right bg-green-100 text-green-900 border-r border-stone-100">{formatINR(viewing.actual_grand_total || viewing.grand_total)}</td>
                            <td className={`px-2 py-2 text-right ${(viewing.variance_amount || 0) > 0 ? 'text-red-700' : (viewing.variance_amount || 0) < 0 ? 'text-green-700' : 'text-stone-500'}`}>{(viewing.variance_amount || 0) > 0 ? '+' : ''}{formatINR(viewing.variance_amount || 0)}</td>
                            <td colSpan="2"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {viewing.notes && (
                    <div className="mt-3 text-xs text-stone-600">
                      <strong>Notes:</strong> {viewing.notes}
                    </div>
                  )}
                  {/* iter-338i: Vendor-uploaded attachments (download links) */}
                  {Array.isArray(viewing.attachments) && viewing.attachments.length > 0 && (
                    <div className="mt-3" data-testid="view-attachments-block">
                      <div className="text-xs font-semibold text-stone-700 mb-1">Attachments ({viewing.attachments.length})</div>
                      <ul className="space-y-1">
                        {viewing.attachments.map((a, i) => (
                          <li key={a.file_id || i} className="flex items-center justify-between text-xs bg-stone-50 border border-stone-200 rounded px-2 py-1.5">
                            <span className="truncate">{a.filename} <span className="text-stone-400">({Math.round((a.size || 0) / 1024)} KB)</span></span>
                            <button
                              type="button"
                              className="text-blue-700 underline hover:text-blue-900"
                              onClick={async () => {
                                try {
                                  const r = await axios.get(
                                    `${API}/vendor-submissions/${viewing.id}/attachments/${a.file_id}`,
                                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob' }
                                  );
                                  const url = URL.createObjectURL(r.data);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = a.filename || 'attachment';
                                  link.click();
                                  URL.revokeObjectURL(url);
                                } catch {}
                              }}
                              data-testid={`view-attachment-${i}-download`}
                            >Download</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {viewing.review_notes && (
                    <div className="mt-2 text-xs p-2 bg-stone-50 rounded border border-stone-200">
                      <strong>Review note:</strong> {viewing.review_notes}
                    </div>
                  )}
                </div>

                {isAdmin && viewing.status === 'submitted' && (
                  <div className="mt-3 flex justify-end gap-2 border-t border-stone-200 pt-3">
                    <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => doReject(viewing)} data-testid="view-reject-btn">
                      <X size={14} className="mr-1" />Reject
                    </Button>
                    <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => doApprove(viewing)} data-testid="view-approve-btn">
                      <Check size={14} className="mr-1" />Approve
                    </Button>
                  </div>
                )}

                {/* iter-311: Add to BOQ — visible for APPROVED submissions on a project */}
                {isAdmin && viewing.status === 'approved' && viewing.project_id && (
                  <div className="mt-3 flex justify-between items-center gap-2 flex-wrap rounded border border-blue-200 bg-blue-50 p-3">
                    <div className="text-xs">
                      <span className="font-semibold text-blue-900">Add this to a BOQ worksheet?</span>{' '}
                      <span className="text-blue-700">Line items will be added under "Actual Site Bills" with Est=0 and Actual={`{qty,rate}`} populated.</span>
                      {viewing.added_to_boq_worksheet_id && (
                        <span className="ml-2 inline-block px-2 py-0.5 bg-green-200 text-green-900 rounded text-[10px]" data-testid="already-added-badge">
                          Already added on {viewing.added_to_boq_at ? new Date(viewing.added_to_boq_at).toLocaleDateString('en-IN') : 'previous date'}
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => { setAddBOQTarget(viewing); fetchProjectWorksheets(viewing.project_id); setAddBOQOpen(true); }}
                      data-testid="open-add-to-boq-btn"
                    >
                      Add to BOQ
                    </Button>
                  </div>
                )}
              </>
            )}
            </div>
          </DialogContent>
        </Dialog>

        {/* iter-311: Add to BOQ dialog */}
        <Dialog open={addBOQOpen} onOpenChange={(o) => !o && setAddBOQOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="add-to-boq-dialog">
            <DialogHeader>
              <DialogTitle>Add to BOQ — {addBOQTarget?.title}</DialogTitle>
              <DialogDescription className="text-xs text-stone-500">
                Choose a target worksheet inside <strong>{addBOQTarget?.project_name}</strong>. Line items will appear under an "Actual Site Bills" header with Est=0 and Actual columns populated.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button type="button"
                  onClick={() => setAddBOQMode('new')}
                  className={`p-3 rounded-md border text-left ${addBOQMode === 'new' ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:border-stone-300'}`}
                  data-testid="add-to-boq-mode-new"
                >
                  <div className="text-sm font-semibold text-stone-800">Create a new worksheet</div>
                  <div className="text-[11px] text-stone-500">Adds a fresh "Vendor Bill — RA N" worksheet inside this project.</div>
                </button>
                <button type="button"
                  onClick={() => setAddBOQMode('existing')}
                  className={`p-3 rounded-md border text-left ${addBOQMode === 'existing' ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:border-stone-300'}`}
                  data-testid="add-to-boq-mode-existing"
                >
                  <div className="text-sm font-semibold text-stone-800">Append to existing worksheet</div>
                  <div className="text-[11px] text-stone-500">Pick an existing worksheet; items appended at the bottom.</div>
                </button>
              </div>

              {addBOQMode === 'new' ? (
                <div>
                  <Label className="text-xs">New worksheet name</Label>
                  <Input
                    value={addBOQName}
                    onChange={e => setAddBOQName(e.target.value)}
                    placeholder={`Vendor Bill — ${addBOQTarget?.title || ''}`}
                    data-testid="add-to-boq-name"
                  />
                </div>
              ) : (
                <div>
                  <Label className="text-xs">Target worksheet</Label>
                  <select
                    value={addBOQWorksheetId}
                    onChange={e => setAddBOQWorksheetId(e.target.value)}
                    className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm"
                    data-testid="add-to-boq-worksheet-select"
                  >
                    <option value="">— Select an existing worksheet —</option>
                    {projectWorksheets.map(w => (
                      <option key={w.id} value={w.id}>{w.name} {w.status === 'published' ? '(published)' : ''}</option>
                    ))}
                  </select>
                  {projectWorksheets.length === 0 && (
                    <p className="text-[11px] text-amber-700 mt-1">No worksheets exist on this project. Use "Create a new worksheet" instead.</p>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setAddBOQOpen(false)} disabled={addBOQSaving}>Cancel</Button>
                <Button type="button" onClick={submitAddToBOQ} disabled={addBOQSaving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-to-boq-confirm">
                  {addBOQSaving ? 'Adding…' : 'Confirm'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* iter-312: Email Preview dialog */}
        <Dialog open={emailOpen} onOpenChange={(o) => !o && setEmailOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="email-preview-dialog">
            <DialogHeader>
              <DialogTitle>Email Preview — {emailTarget?.title}</DialogTitle>
              <DialogDescription className="text-xs text-stone-500">
                Send a styled HTML preview with a PDF attachment. Vendors can use this to email a copy of their {emailTarget?.submission_type === 'bill' ? 'Running Bill' : (emailTarget?.submission_type || 'submission')} for review before final submission.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">To <span className="text-red-500">*</span></Label>
                <Input type="email" value={emailForm.to_email} onChange={e => setEmailForm({ ...emailForm, to_email: e.target.value })} placeholder="recipient@company.com" data-testid="email-preview-to" />
              </div>
              <div>
                <Label className="text-xs">CC (optional)</Label>
                <Input type="email" value={emailForm.cc_email} onChange={e => setEmailForm({ ...emailForm, cc_email: e.target.value })} placeholder="cc@company.com" data-testid="email-preview-cc" />
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="email-preview-subject" />
              </div>
              <div>
                <Label className="text-xs">Message (optional, shown above the table)</Label>
                <Textarea rows={3} value={emailForm.message} onChange={e => setEmailForm({ ...emailForm, message: e.target.value })} placeholder="Hi, please find attached our quote for your review..." data-testid="email-preview-message" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEmailOpen(false)} disabled={emailSending}>Cancel</Button>
                <Button type="button" onClick={sendEmailPreview} disabled={emailSending} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="email-preview-send-btn">
                  <Mail size={12} className="mr-1" />{emailSending ? 'Sending…' : 'Send'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* iter-312: Generate Ad-hoc Vendor Link */}
        <Dialog open={genOpen} onOpenChange={(o) => !o && setGenOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="generate-link-dialog">
            <DialogHeader>
              <DialogTitle>Generate Ad-hoc Vendor Link</DialogTitle>
              <DialogDescription className="text-xs text-stone-500">
                The vendor will land on a public page where they enter their name + email, fill the BOQ-style form, and submit — no signup required.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type *</Label>
                  <select value={genForm.submission_type} onChange={e => setGenForm({ ...genForm, submission_type: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="gen-type">
                    {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Expiry (days)</Label>
                  <select value={genForm.expiry_days} onChange={e => setGenForm({ ...genForm, expiry_days: +e.target.value })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="gen-expiry">
                    {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d} days</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Project (optional)</Label>
                <select value={genForm.project_id} onChange={e => setGenForm({ ...genForm, project_id: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="gen-project">
                  <option value="">— Standalone (no project) —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Internal note (optional)</Label>
                <Input value={genForm.note} onChange={e => setGenForm({ ...genForm, note: e.target.value })} placeholder="e.g., For Mr. Sharma — false ceiling quote" data-testid="gen-note" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setGenOpen(false)} disabled={genSaving}>Cancel</Button>
                <Button type="button" onClick={createShareLink} disabled={genSaving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="gen-create-btn">
                  {genSaving ? 'Generating…' : 'Generate Link'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* iter-312: Email an existing link to a vendor */}
        <Dialog open={linkEmailOpen} onOpenChange={(o) => !o && setLinkEmailOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="link-email-dialog">
            <DialogHeader>
              <DialogTitle>Email Link to Vendor</DialogTitle>
              <DialogDescription className="text-xs text-stone-500">
                Sends the public submission URL to the vendor. They will see a clean, branded email with a clickable button.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-stone-50 border border-stone-200 rounded p-2 text-[11px] text-stone-600">
                <strong>URL:</strong> {linkEmailTarget?.public_url}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Recipient email *</Label>
                  <Input type="email" value={linkEmailForm.to_email} onChange={e => setLinkEmailForm({ ...linkEmailForm, to_email: e.target.value })} placeholder="vendor@company.com" data-testid="link-email-to" />
                </div>
                <div>
                  <Label className="text-xs">Recipient name (optional)</Label>
                  <Input value={linkEmailForm.recipient_name} onChange={e => setLinkEmailForm({ ...linkEmailForm, recipient_name: e.target.value })} placeholder="Vendor's name" data-testid="link-email-name" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Message (optional)</Label>
                <Textarea rows={4} value={linkEmailForm.message} onChange={e => setLinkEmailForm({ ...linkEmailForm, message: e.target.value })} placeholder="Hi, please submit your quote using the link below..." data-testid="link-email-message" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setLinkEmailOpen(false)} disabled={linkEmailSending}>Cancel</Button>
                <Button type="button" onClick={sendLinkEmail} disabled={linkEmailSending} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="link-email-send-btn">
                  <Mail size={12} className="mr-1" />{linkEmailSending ? 'Sending…' : 'Send Link'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* iter-312q: Attach standalone submission to a project */}
        <Dialog open={attachOpen} onOpenChange={(o) => !o && setAttachOpen(false)}>
          <DialogContent className="max-w-md" data-testid="attach-to-project-dialog">
            <DialogHeader>
              <DialogTitle>Attach Submission to a Project</DialogTitle>
              <DialogDescription className="text-xs text-stone-500">
                {attachTarget?.title ? <>Linking <strong>{attachTarget.title}</strong> to a project will move it under that project's submissions list. You can detach later by editing the submission.</> : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Project *</Label>
                <select
                  value={attachProjectId}
                  onChange={e => setAttachProjectId(e.target.value)}
                  className="w-full h-9 text-sm border border-stone-200 rounded px-2 outline-none focus:ring-1 focus:ring-blue-300"
                  data-testid="attach-project-select"
                >
                  <option value="">Select a project…</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.project_number ? ` (${p.project_number})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setAttachOpen(false)}>Cancel</Button>
                <Button onClick={submitAttach} disabled={!attachProjectId || attachSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="attach-submit-btn">
                  {attachSaving ? 'Attaching…' : 'Attach to Project'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* iter-312t: Link a vendor quote to ADDITIONAL projects (multi-select) */}
        <Dialog open={linkOpen} onOpenChange={(o) => !o && setLinkOpen(false)}>
          <DialogContent className="max-w-lg !max-h-[88vh] flex flex-col overflow-hidden p-0" data-testid="link-to-projects-dialog">
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-stone-200 bg-white flex-shrink-0">
              <DialogTitle>Apply this quote to additional projects</DialogTitle>
              <DialogDescription className="text-xs text-stone-500">
                {linkTarget?.title ? <>The quote <strong>{linkTarget.title}</strong> already belongs to <span className="font-mono">{linkTarget.project_name || 'a project'}</span>. Selected additional projects can now reference the same items + rates — no need to re-request from the vendor.</> : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2 min-h-0">
              {(linkTarget?.linked_project_ids || []).length > 0 && (
                <div className="text-[11px] text-stone-500 mb-2">
                  Already linked to {linkTarget.linked_project_ids.length} additional project{linkTarget.linked_project_ids.length === 1 ? '' : 's'}.
                </div>
              )}
              {projects
                .filter(p => p.id !== linkTarget?.project_id && !(linkTarget?.linked_project_ids || []).includes(p.id))
                .map(p => {
                  const checked = linkSelected.includes(p.id);
                  return (
                    <label key={p.id} className={`flex items-center gap-2 px-3 py-2 rounded border ${checked ? 'border-blue-400 bg-blue-50' : 'border-stone-200 hover:bg-stone-50'} cursor-pointer text-sm`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setLinkSelected(prev => checked ? prev.filter(x => x !== p.id) : [...prev, p.id]);
                        }}
                        className="w-4 h-4 accent-blue-600"
                        data-testid={`link-project-${p.id}`}
                      />
                      <span className="flex-1">{p.name}</span>
                      {p.project_number && <span className="text-[10px] font-mono text-stone-500">{p.project_number}</span>}
                    </label>
                  );
                })}
              {projects.filter(p => p.id !== linkTarget?.project_id && !(linkTarget?.linked_project_ids || []).includes(p.id)).length === 0 && (
                <div className="text-stone-400 text-sm text-center py-6">No more projects available to link.</div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-stone-200 bg-white flex items-center justify-end gap-2 flex-shrink-0">
              <Button variant="outline" onClick={() => setLinkOpen(false)} disabled={linkSaving}>Cancel</Button>
              <Button onClick={submitLink} disabled={linkSelected.length === 0 || linkSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="link-submit-btn">
                {linkSaving ? 'Linking…' : `Link to ${linkSelected.length} project${linkSelected.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        {/* iter-313f: Convert-to-Running-Bill result dialog */}
        <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
          <DialogContent className="max-w-lg" data-testid="convert-bill-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-700">
                <Receipt size={20} /> Running Bill Created
              </DialogTitle>
              <DialogDescription>
                The vendor can now fill Actual (Site) figures and add extra line items. Original Quote items are frozen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
                <div className="text-xs text-emerald-700 uppercase tracking-wide mb-1">Bill #</div>
                <div className="font-semibold text-emerald-900">{convertResult?.submission_number || convertResult?.submission_id?.slice(0, 8)}</div>
              </div>
              <div>
                <Label className="text-xs text-stone-500">Vendor public link</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={
                      convertResult?.public_path
                        ? `${window.location.origin}${convertResult.public_path}`
                        : convertResult?.public_url || ''
                    }
                    readOnly
                    className="font-mono text-xs"
                    data-testid="convert-bill-link-input"
                  />
                  <Button onClick={doCopyBillLink} variant="outline" size="sm" data-testid="convert-bill-copy-btn">Copy</Button>
                </div>
                <p className="text-[10px] text-stone-400 mt-1">Tip: this link points to your current environment. The vendor email uses the same domain you're viewing from.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setConvertOpen(false)}>Close</Button>
                <Button onClick={doEmailBillToVendor} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="convert-bill-email-btn">
                  Email to Vendor
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

export default function VendorSubmissionsPage() {
  return (
    <Layout>
      <VendorSubmissionsContent embedded={false} />
    </Layout>
  );
}


// iter-315d: tiny status chip for Running Bill rows
function RunningBillStatusBadge({ meta, testId }) {
  if (!meta) return null;
  const sinceMs = meta.since ? Date.parse(meta.since) : 0;
  const diffMs = Math.max(0, Date.now() - sinceMs);
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  let agoLabel = 'just now';
  if (days >= 1) agoLabel = `${days}d ago`;
  else if (hours >= 1) agoLabel = `${hours}h ago`;
  else if (minutes >= 1) agoLabel = `${minutes}m ago`;
  const cfg = {
    notified: { emoji: '📨', text: 'Notified', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    opened: { emoji: '✅', text: 'Vendor opened', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    followed_up: { emoji: '🔔', text: 'Followed-up', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    submitted: { emoji: '💬', text: 'Vendor submitted', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  };
  const c = cfg[meta.state] || cfg.notified;
  const opens = meta.opens != null ? meta.opens : 0;
  return (
    <div
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 mt-1 rounded border text-[10px] font-semibold ${c.cls}`}
      title={`${c.text} · ${agoLabel}${meta.state === 'notified' || meta.state === 'opened' ? ` · ${opens} open${opens === 1 ? '' : 's'}` : ''}`}
      data-testid={testId}
    >
      <span>{c.emoji}</span>
      <span>{c.text}</span>
      <span className="opacity-60">·</span>
      <span>{agoLabel}</span>
      {(meta.state === 'notified' || meta.state === 'opened') && (
        <>
          <span className="opacity-60">·</span>
          <span>{opens} open{opens === 1 ? '' : 's'}</span>
        </>
      )}
    </div>
  );
}
